/**
 * Shared bridge for VYSOR wizard ↔ agent chat responses (TurboChat + TurboChatPage).
 */
import type { SSOPrefillData } from '../types';
import type { VysorAppCatalogItem } from './vysorCatalog';
import type { WizardField } from './vysorFlows';
import type { WizardState, VysorWizardContext } from './vysorWizardEngine';
import { resumeWizardAfterAgentPrefill } from './vysorWizardAssistGates';

export type WizardAgentQuickOption = {
  id: string;
  label: string;
  description?: string;
  value?: string;
};

export type ProcessWizardAgentResponseResult = {
  /** Wizard step advanced via prefill resume. */
  wizardResumed: boolean;
  /** Caller should run advanceWizard with this answer after resume. */
  advanceAnswer?: string;
  /** Prefill could not resume wizard — show submit SSO chips in free chat. */
  pendingSsoPrefill?: SSOPrefillData;
  /** Whether SSO prefill is ready to submit without modal. */
  canSubmitSso?: boolean;
};

type WizardAgentHandlers = {
  setWizard: (wizard: WizardState | null) => void;
  addBotMessage: (text: string, options?: WizardAgentQuickOption[]) => void;
  showWizardField: (field: WizardField, collected: Record<string, string>) => void;
};

/**
 * Handle POST /assistant/chat payload while a wizard may be active.
 * Keeps wizard chips intact for text-only agent replies during assist steps.
 */
export function processWizardAgentChatResponse(
  data: { message?: string; prefill?: SSOPrefillData; options?: WizardAgentQuickOption[] } | null | undefined,
  activeWizard: WizardState | null,
  catalog: VysorAppCatalogItem[],
  context: VysorWizardContext,
  handlers: WizardAgentHandlers,
  canSubmitSsoPrefill: (prefill: SSOPrefillData) => boolean,
): ProcessWizardAgentResponseResult {
  const result: ProcessWizardAgentResponseResult = { wizardResumed: false };
  if (!data) {
    handlers.addBotMessage(
      "I couldn't read the assistant response. Please try again, or say **list pending SSO**.",
    );
    return result;
  }

  if (data.prefill) {
    const prefill = data.prefill;
    const resume = resumeWizardAfterAgentPrefill(
      activeWizard,
      prefill,
      catalog,
      context,
      {
        setWizard: handlers.setWizard,
        addBotMessage: msg => handlers.addBotMessage(msg),
        showField: (field, collected) => handlers.showWizardField(field, collected),
      },
    );
    if (resume.resumed) {
      result.wizardResumed = true;
      if ('advanceAnswer' in resume && resume.advanceAnswer) {
        result.advanceAnswer = resume.advanceAnswer;
      }
      return result;
    }
    result.pendingSsoPrefill = prefill;
    result.canSubmitSso = canSubmitSsoPrefill(prefill);
    const canSubmit = result.canSubmitSso;
    handlers.addBotMessage(
      (data.message || "I've gathered SSO details for you.") +
        (canSubmit
          ? '\n\nTap **Submit SSO configuration** when ready — everything stays in this chat.'
          : '\n\nI still need the application and environment confirmed before I can submit.'),
      canSubmit
        ? [
            { id: 'submit_sso', label: 'Submit SSO configuration' },
            { id: 'continue_chat', label: 'Continue in chat' },
          ]
        : [{ id: 'continue_chat', label: 'Continue in chat' }],
    );
    return result;
  }

  handlers.addBotMessage(
    data.message || "I'm sorry, I couldn't generate a response. Please try again.",
    activeWizard ? undefined : data.options,
  );
  return result;
}
